import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles, Users, TrendingUp, Gift, Phone, QrCode, MessageSquare, Send, CheckCircle2 } from "lucide-react";
import { useHideEmergentBadge } from "@/hooks/useHideEmergentBadge";
import SupportChatWidget from "@/components/SupportChatWidget";

export default function Landing() {
  useHideEmergentBadge();
  return (
    <div className="min-h-screen bg-white text-[#0a0a0a]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-[#e5e5eb]/70">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
            <div className="w-7 h-7 rounded-lg bg-[#002fa7] flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display font-extrabold text-lg tracking-tight">SparkLoop</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-[#5a5a65]">
            <a href="#how" className="hover:text-[#0a0a0a]">How it works</a>
            <a href="#business" className="hover:text-[#0a0a0a]">Features</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login" data-testid="nav-login">
              <Button className="bg-[#002fa7] hover:bg-[#002585] rounded-full text-white px-5">
                Sign in <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        {/* Ambient background flourishes — radial brand glow + soft grid */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(60% 50% at 70% 30%, rgba(0,47,167,0.10) 0%, rgba(0,47,167,0) 60%), radial-gradient(40% 40% at 20% 80%, rgba(0,158,96,0.06) 0%, rgba(0,158,96,0) 60%)",
          }}
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none opacity-[0.025]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(0,0,0,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.6) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />

        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-28 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6 fade-up">
            <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.24em] text-[#002fa7] mb-6">
              <span className="w-8 h-px bg-[#002fa7]" />
              A loyalty engine for small business
            </div>
            <h1 className="font-display text-5xl sm:text-7xl font-extrabold tracking-tighter leading-[0.95] mb-6">
              Turn visits<br />
              <span className="text-[#002fa7]">into value.</span>
            </h1>
            <p className="text-lg text-[#5a5a65] leading-relaxed max-w-xl mb-8">
              A rewards program as simple as a phone number. Customers earn points on every visit, redeem
              rewards they love, and keep coming back. No app downloads. No plastic cards.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/login" data-testid="hero-login-cta">
                <Button className="bg-[#002fa7] hover:bg-[#002585] rounded-full text-white px-7 py-6 text-base font-semibold">
                  Sign in <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>

          {/* Tablet kiosk mockup + supporting loyalty cards */}
          <div className="md:col-span-6 relative h-[480px] sm:h-[540px] fade-up" style={{ animationDelay: "120ms" }}>
            {/* Soft platform shadow under the tablet */}
            <div
              aria-hidden="true"
              className="absolute bottom-4 left-1/2 -translate-x-1/2 w-[78%] h-8 rounded-full bg-[#002fa7]/15 blur-2xl"
            />

            {/* Tablet device — landscape iPad-style frame */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div
                className="relative w-[88%] sm:w-[440px] aspect-[4/3] rounded-[2.2rem] bg-gradient-to-br from-[#1a1a1f] to-[#0a0a0d] p-3 shadow-[0_30px_60px_-15px_rgba(0,47,167,0.35),0_18px_36px_-12px_rgba(0,0,0,0.3)]"
                style={{ transform: "perspective(1200px) rotateY(-6deg) rotateX(4deg)" }}
              >
                {/* Tablet screen */}
                <div className="w-full h-full rounded-[1.6rem] bg-[#002fa7] overflow-hidden flex flex-col">
                  {/* Notch bar */}
                  <div className="flex items-center justify-between px-5 pt-2 pb-1 text-[8px] uppercase tracking-[0.2em] font-bold text-white/70">
                    <span>9:41</span>
                    <span className="flex items-center gap-1">
                      <span className="w-1 h-1 rounded-full bg-white/70" />
                      <span className="w-1 h-1 rounded-full bg-white/70" />
                      <span className="w-1 h-1 rounded-full bg-white/70" />
                    </span>
                  </div>

                  {/* Kiosk content — left brand panel, right phone-entry card */}
                  <div className="flex-1 grid grid-cols-[1fr_1.2fr] gap-2 p-3">
                    <div className="rounded-2xl bg-[#001a55] p-3 flex flex-col justify-end text-white">
                      <div className="text-[8px] uppercase tracking-[0.2em] font-bold opacity-70 mb-1">
                        Rosa's Pizzeria
                      </div>
                      <div className="font-display text-xl font-extrabold leading-tight">
                        Tap. Earn.<br />Repeat.
                      </div>
                    </div>
                    <div className="rounded-2xl bg-white p-3 flex flex-col">
                      <div className="inline-flex self-start items-center gap-1 text-[8px] uppercase tracking-[0.16em] font-bold px-2 py-0.5 rounded-full bg-[#002fa7]/10 text-[#002fa7] mb-1.5">
                        <Sparkles className="w-2 h-2" /> Earn points
                      </div>
                      <div className="font-display text-[15px] font-extrabold leading-tight mb-1.5 text-[#0a0a0a]">
                        Enter your phone.
                      </div>
                      {/* Mock phone display */}
                      <div className="h-6 rounded-md bg-[#f9f9fb] border border-[#e5e5eb] flex items-center px-2 font-mono-data text-[10px] text-[#0a0a0a] mb-1.5">
                        +1 (555) ___
                      </div>
                      {/* Mini numpad */}
                      <div className="grid grid-cols-3 gap-1">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                          <div
                            key={n}
                            className="aspect-square rounded-md bg-white border border-[#e5e5eb] flex items-center justify-center font-mono-data font-bold text-[#002fa7] text-[10px]"
                          >
                            {n}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Screen glare highlight */}
                <div
                  aria-hidden="true"
                  className="absolute inset-3 rounded-[1.6rem] pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 35%, rgba(255,255,255,0) 65%, rgba(255,255,255,0.08) 100%)",
                  }}
                />
              </div>
            </div>

            {/* Floating loyalty card — top right */}
            <div className="absolute top-2 right-0 sm:-right-4 w-44 sm:w-52 rounded-2xl bg-[#002fa7] text-white p-4 shadow-2xl rotate-[8deg] hover:rotate-[3deg] transition-transform duration-500 z-10">
              <div className="text-[9px] uppercase tracking-[0.2em] opacity-70 font-bold">Blue Bottle Coffee</div>
              <div className="mt-5 font-display text-3xl font-extrabold tracking-tight">1,240</div>
              <div className="text-[9px] uppercase tracking-[0.2em] opacity-80 mt-0.5">pts balance</div>
            </div>

            {/* Floating "scan to join" chip — bottom left */}
            <div className="absolute bottom-6 left-0 sm:-left-2 w-48 rounded-2xl bg-white border border-[#e5e5eb] p-3.5 shadow-lg -rotate-[6deg] hover:rotate-[-1deg] transition-transform duration-500 z-10">
              <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.2em] text-[#5a5a65] font-semibold">
                <QrCode className="w-3 h-3" /> Scan to join
              </div>
              <div className="mt-2 font-display text-xl font-extrabold leading-tight text-[#0a0a0a]">
                +15 pts<br />
                <span className="text-[#002fa7]">on your visit</span>
              </div>
            </div>
          </div>
        </div>

        {/* Marquee */}
        <div className="border-y border-[#e5e5eb] bg-[#f9f9fb] overflow-hidden">
          <div className="flex animate-marquee whitespace-nowrap py-4">
            {Array.from({ length: 2 }).map((_, rep) => (
              <div key={rep} className="flex gap-16 pr-16 text-sm uppercase tracking-[0.24em] font-semibold text-[#5a5a65]">
                <span>Coffee Shops</span>
                <span className="text-[#002fa7]">◆</span>
                <span>Salons</span>
                <span className="text-[#002fa7]">◆</span>
                <span>Restaurants</span>
                <span className="text-[#002fa7]">◆</span>
                <span>Retail</span>
                <span className="text-[#002fa7]">◆</span>
                <span>Fitness Studios</span>
                <span className="text-[#002fa7]">◆</span>
                <span>Bakeries</span>
                <span className="text-[#002fa7]">◆</span>
                <span>Car Washes</span>
                <span className="text-[#002fa7]">◆</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="text-xs uppercase tracking-[0.24em] text-[#002fa7] font-semibold mb-4">
              How it works
            </div>
            <h2 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05]">
              Three steps.<br />Zero friction.
            </h2>
          </div>
          <div className="md:col-span-8 grid sm:grid-cols-3 gap-4">
            {[
              { n: "01", icon: Phone, t: "Enter phone", d: "Customers type their number at checkout. That's their card." },
              { n: "02", icon: Sparkles, t: "Earn points", d: "Every visit adds points. Set it once, it runs forever." },
              { n: "03", icon: Gift, t: "Redeem rewards", d: "Pick from your catalog. Customers feel the value." },
            ].map((s) => (
              <div
                key={s.n}
                className="group bg-white border border-[#e5e5eb] rounded-2xl p-6 hover:border-[#0a0a0a] transition-all"
              >
                <div className="flex items-start justify-between mb-10">
                  <span className="font-mono-data text-sm text-[#5a5a65]">{s.n}</span>
                  <s.icon className="w-5 h-5 text-[#002fa7]" />
                </div>
                <div className="font-display text-xl font-bold mb-2">{s.t}</div>
                <div className="text-sm text-[#5a5a65] leading-relaxed">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Split features */}
      <section className="border-y border-[#e5e5eb] bg-[#f9f9fb]">
        <div className="max-w-7xl mx-auto px-6 py-24 grid md:grid-cols-2 gap-10">
          <div id="business" className="bg-white rounded-2xl border border-[#e5e5eb] p-10 relative overflow-hidden">
            <div className="text-xs uppercase tracking-[0.24em] text-[#002fa7] font-semibold mb-4">
              For Business
            </div>
            <h3 className="font-display text-3xl font-extrabold mb-4 tracking-tight">
              Own your repeat revenue.
            </h3>
            <p className="text-[#5a5a65] mb-8 leading-relaxed">
              Replace punch cards with a modern dashboard. Know who your best customers are, what they love,
              and why they come back.
            </p>
            <ul className="space-y-3 text-sm">
              {[
                { icon: Users, t: "Customer list with visit history" },
                { icon: TrendingUp, t: "Live stats: points issued, visits, redemptions" },
                { icon: Gift, t: "Custom rewards catalog" },
                { icon: MessageSquare, t: "SMS marketing — text your regulars deals & reminders" },
                { icon: QrCode, t: "Shareable QR + public sign-up link" },
              ].map((f) => (
                <li key={f.t} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#f0f0f4] flex items-center justify-center">
                    <f.icon className="w-4 h-4 text-[#002fa7]" />
                  </div>
                  <span className="font-medium">{f.t}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <Link to="/login" data-testid="feature-business-cta">
                <Button className="bg-[#002fa7] hover:bg-[#002585] text-white rounded-full px-6">
                  Open merchant portal <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>

          <div id="customer" className="bg-[#0a0a0a] text-white rounded-2xl p-10 relative overflow-hidden">
            <div className="text-xs uppercase tracking-[0.24em] text-[#ffb800] font-semibold mb-4">
              For Customers
            </div>
            <h3 className="font-display text-3xl font-extrabold mb-4 tracking-tight">
              Every visit should reward you.
            </h3>
            <p className="text-white/70 mb-8 leading-relaxed">
              One wallet, every local business you love. Check your points, see what you can redeem, and
              never miss a perk again.
            </p>
            <div className="grid grid-cols-2 gap-3 mb-8">
              <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">Balance</div>
                <div className="font-display text-3xl font-extrabold mt-2">1,240</div>
                <div className="text-xs text-white/60 mt-1">Blue Bottle Coffee</div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">Next reward</div>
                <div className="font-display text-3xl font-extrabold mt-2">60 pts</div>
                <div className="text-xs text-white/60 mt-1">Free pastry</div>
              </div>
            </div>
            <Link to="/login" data-testid="feature-customer-cta">
              <Button className="bg-white text-[#0a0a0a] hover:bg-white/90 rounded-full px-6 font-semibold">
                Staff sign-in <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* SMS Marketing showcase */}
      <section id="sms" className="relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(50% 50% at 80% 20%, rgba(0,47,167,0.08) 0%, rgba(0,47,167,0) 60%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 py-24 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6">
            <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.24em] text-[#002fa7] mb-5">
              <MessageSquare className="w-3.5 h-3.5" /> SMS Marketing · Included
            </div>
            <h2 className="font-display text-5xl sm:text-6xl font-extrabold tracking-tighter leading-[0.95] mb-6">
              Text your<br />regulars.<br />
              <span className="text-[#002fa7]">Bring them back.</span>
            </h2>
            <p className="text-lg text-[#5a5a65] leading-relaxed max-w-xl mb-8">
              Type a message, tap send. Reach every customer who opted in at your kiosk — in 30 seconds.
              Perfect for slow days, new menu drops, and "we miss you" nudges.
            </p>
            <ul className="space-y-3 mb-8">
              {[
                "Customers opt in with one tap at the kiosk",
                "Use your own Twilio account — pay only ~$0.008 per text (no markup from us)",
                "STOP / unsubscribe handled automatically",
                "See cost estimate before you hit send",
              ].map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-[#009e60] flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                  <span className="leading-relaxed">{f}</span>
                </li>
              ))}
            </ul>
            <Link to="/login" data-testid="sms-section-cta">
              <Button className="bg-[#002fa7] hover:bg-[#002585] text-white rounded-full px-7 py-6 text-base font-semibold">
                Try SparkLoop <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>

          {/* Phone mockup of an incoming SMS */}
          <div className="md:col-span-6 relative h-[520px]">
            {/* Soft platform shadow */}
            <div
              aria-hidden="true"
              className="absolute bottom-6 left-1/2 -translate-x-1/2 w-[60%] h-8 rounded-full bg-[#002fa7]/15 blur-2xl"
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div
                className="relative w-[280px] sm:w-[320px] aspect-[9/19] rounded-[3rem] bg-gradient-to-br from-[#1a1a1f] to-[#0a0a0d] p-2.5 shadow-[0_30px_60px_-15px_rgba(0,47,167,0.35),0_18px_36px_-12px_rgba(0,0,0,0.3)]"
                style={{ transform: "perspective(1400px) rotateY(-8deg) rotateX(2deg)" }}
              >
                {/* Phone screen */}
                <div className="w-full h-full rounded-[2.4rem] bg-[#f9f9fb] overflow-hidden flex flex-col">
                  {/* Status bar with notch */}
                  <div className="relative flex items-center justify-between px-6 pt-3 pb-1 text-[10px] font-bold text-[#0a0a0a]">
                    <span>9:41</span>
                    <div className="absolute left-1/2 -translate-x-1/2 top-2 w-20 h-5 rounded-full bg-[#0a0a0a]" />
                    <span className="flex items-center gap-1">
                      <span className="w-1 h-1 rounded-full bg-[#0a0a0a]" />
                      <span className="w-1 h-1 rounded-full bg-[#0a0a0a]" />
                      <span className="w-1 h-1 rounded-full bg-[#0a0a0a]" />
                    </span>
                  </div>

                  {/* Messages header */}
                  <div className="flex items-center gap-3 px-4 pt-4 pb-2 border-b border-[#e5e5eb]">
                    <div className="w-9 h-9 rounded-full bg-[#002fa7] text-white flex items-center justify-center font-bold text-sm">R</div>
                    <div>
                      <div className="font-display font-extrabold text-sm text-[#0a0a0a]">Rosa's Pizzeria</div>
                      <div className="text-[10px] text-[#5a5a65]">Just now</div>
                    </div>
                  </div>

                  {/* SMS bubble */}
                  <div className="flex-1 px-4 py-4 space-y-2">
                    <div className="bg-white border border-[#e5e5eb] rounded-2xl rounded-tl-md p-3 max-w-[85%] shadow-sm">
                      <p className="text-[13px] text-[#0a0a0a] leading-snug">
                        🍕 Happy Friday! 20% off all pizzas tonight only — show this text at the counter. See you soon!
                      </p>
                      <p className="text-[10px] text-[#5a5a65] mt-2 leading-tight">
                        Reply STOP to opt out.
                      </p>
                    </div>
                    <div className="text-[10px] text-[#5a5a65] pl-2">Delivered</div>
                  </div>
                </div>

                {/* Glare */}
                <div
                  aria-hidden="true"
                  className="absolute inset-2.5 rounded-[2.4rem] pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 30%, rgba(255,255,255,0) 70%, rgba(255,255,255,0.08) 100%)",
                  }}
                />
              </div>
            </div>

            {/* Floating "+24 redemptions" stat */}
            <div className="absolute top-8 right-2 sm:right-8 w-44 rounded-2xl bg-white border border-[#e5e5eb] p-4 shadow-2xl rotate-[6deg] z-10">
              <div className="text-[9px] uppercase tracking-[0.2em] text-[#5a5a65] font-bold mb-1.5">After last blast</div>
              <div className="font-display text-3xl font-extrabold leading-none text-[#002fa7]">+24</div>
              <div className="text-[10px] uppercase tracking-[0.16em] text-[#0a0a0a] mt-1 font-semibold">visits same week</div>
            </div>

            {/* Floating compose chip */}
            <div className="absolute bottom-8 left-0 sm:left-4 w-52 rounded-2xl bg-[#002fa7] text-white p-3.5 shadow-2xl -rotate-[5deg] z-10">
              <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.2em] opacity-80 font-bold mb-1.5">
                <Send className="w-3 h-3" /> Sending to 142
              </div>
              <div className="font-mono-data text-[11px] opacity-90 leading-snug">~$1.14 est. cost</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-6 py-28 text-center">
        <h2 className="font-display text-5xl sm:text-6xl font-extrabold tracking-tighter leading-[0.95]">
          Keep them <span className="text-[#002fa7]">coming back.</span>
        </h2>
        <p className="text-[#5a5a65] text-lg max-w-2xl mx-auto mt-6 mb-8">
          Set up your rewards program in under two minutes. No credit card required.
        </p>
        <Link to="/login" data-testid="cta-start-now">
          <Button className="bg-[#002fa7] hover:bg-[#002585] text-white rounded-full px-8 py-6 text-base font-semibold">
            Sign in <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#e5e5eb]">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-[#5a5a65]">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded bg-[#002fa7] flex items-center justify-center">
              <Sparkles className="w-3 h-3 text-white" strokeWidth={3} />
            </div>
            <span className="font-display font-bold text-[#0a0a0a]">SparkLoop</span>
            <span>— loyalty reimagined.</span>
          </div>
          <div className="flex items-center gap-5 text-xs font-semibold uppercase tracking-[0.16em]">
            <Link to="/privacy" className="hover:text-[#0a0a0a]" data-testid="footer-privacy-link">Privacy</Link>
            <Link to="/terms" className="hover:text-[#0a0a0a]" data-testid="footer-terms-link">Terms</Link>
            <Link to="/contact" className="hover:text-[#0a0a0a]" data-testid="footer-contact-link">Contact</Link>
          </div>
          <div className="font-mono-data text-xs uppercase tracking-[0.24em]">© 2026 SparkLoop</div>
        </div>
      </footer>
      <SupportChatWidget />
    </div>
  );
}
